import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  DEFAULT_OG_IMAGE,
  SITE_NAME,
  SITE_URL,
  TWITTER_HANDLE,
  getRouteSeo,
  organizationJsonLd,
  professionalServiceJsonLd,
  webPageJsonLd,
  websiteJsonLd,
} from "@/seo/config";

function upsertMeta(attr, key, content) {
  if (content == null || content === "") return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function upsertJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!data) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

/**
 * Per-route SEO: title, description, canonical, Open Graph, Twitter, JSON-LD.
 * Renders nothing — updates document head on route change.
 */
export default function Seo() {
  const { pathname } = useLocation();

  useEffect(() => {
    const seo = getRouteSeo(pathname);
    const canonical = seo.path ? `${SITE_URL}${seo.path}` : SITE_URL;
    const title = seo.title;
    const description = seo.description;
    const robots = seo.noindex ? "noindex, nofollow" : "index, follow";

    document.title = title;

    upsertMeta("name", "description", description);
    upsertMeta("name", "robots", robots);
    upsertMeta("name", "author", SITE_NAME);
    upsertMeta(
      "name",
      "keywords",
      "shipping automation, logistics AI, freight operations, maritime workflow automation, document processing, AI automation, harbrflow",
    );

    upsertLink("canonical", canonical);

    upsertMeta("property", "og:site_name", SITE_NAME);
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", canonical);
    upsertMeta("property", "og:image", DEFAULT_OG_IMAGE);
    upsertMeta("property", "og:image:alt", title);
    upsertMeta("property", "og:locale", "en_US");

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:site", TWITTER_HANDLE);
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", DEFAULT_OG_IMAGE);

    upsertJsonLd("seo-webpage", webPageJsonLd(seo));

    if (pathname === "/") {
      upsertJsonLd("seo-organization", organizationJsonLd());
      upsertJsonLd("seo-website", websiteJsonLd());
      upsertJsonLd("seo-service", professionalServiceJsonLd());
    } else {
      upsertJsonLd("seo-organization", null);
      upsertJsonLd("seo-website", null);
      upsertJsonLd("seo-service", null);
    }
  }, [pathname]);

  return null;
}
